import React, { useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Heading3 } from '@/components/ui/typography'
import { Divider } from 'antd'
import { LabeledValue } from 'antd/es/select'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import {
  FormBaseAntSelectWrapper,
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseNavigationHeader,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

import { NameDto } from '../../../../../api/Common/Types'
import { BankValidationDto } from '../../../../../api/NPayments/Types/BankValidationDto'
import {
  AddClientBankAccount,
  ClientBankAccount,
  UpdateClientBankAccount,
} from '../../../../../api/NPayments/Types/ClientBankAccount'
import { LinkIcon } from '../../../../../components/Icon'
import { Option, Select } from '../../../../../components/Select'
import { validateField } from '../../../../../utils/fieldValidation'
import { truncateString } from '../../../../../utils/string.utils'
import { FormErrorMessage } from '../../../../Administration/Administration.styles'

const AddEditBankAccountFormUI: React.FC<
  FormikProps<AddClientBankAccountFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, setSubmitting, isValid, isSubmitting, errors, values } = props

  const isEdit = useMemo(() => !!props.currentBankAccount, [props.currentBankAccount])

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  const handleCancel = () => {
    props.cancel()
  }

  return (
    <FormBaseContainer>
      <Form>
        <FormBaseNavigationHeader style={{ justifyContent: 'flex-start' }}>
          <Heading3>{isEdit ? 'Edit Bank Account' : 'Add New Bank Account'}</Heading3>
        </FormBaseNavigationHeader>
        <Divider />
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Bank Name</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field data-test='user-bank-name-field' type='text' name='bankName' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.bankName && <FormErrorMessage>{errors.bankName}</FormErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Bank address</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field data-test='user-bank-account-address-field' type='text' name='bankAddress' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.bankAddress && <FormErrorMessage>{errors.bankAddress}</FormErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Beneficiary Name</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field data-test='user-account-name-field' type='text' name='accountName' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.bankAccount && <FormErrorMessage>{errors.accountName}</FormErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>IBAN/Account Number</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field data-test='user-bank-account-iban-field' type='text' name='bankAccount' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.bankAccount && <FormErrorMessage>{errors.bankAccount}</FormErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Bank Identification Number (SWIFT)</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field data-test='user-bank-account-swift-field' type='text' name='bankCode' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.bankCode && <FormErrorMessage>{errors.bankCode}</FormErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Branch Number / Sort Code</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field data-test='user-bank-account-sort-field' type='text' name='codeSort' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.codeSort && <FormErrorMessage>{errors.codeSort}</FormErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Additional Comment</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field data-test='user-bank-account-comment-field' type='text' name='comment' />
            </FormBaseFieldGroup>
          </FormBaseRow>
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Document</FormBaseFieldLabel>
            <FormBaseAntSelectWrapper>
              <FormBaseFieldGroup>
                <Select
                  labelInValue={true}
                  placeholder='Document'
                  value={values.document}
                  allowClear={true}
                  showSearch
                  onChange={(value: LabeledValue) => {
                    if (!value) {
                      return props.setFieldValue('document', null)
                    }
                    props.setFieldValue('document', value)
                  }}
                >
                  {(props.documents || []).map((document) => (
                    <Option value={document.id} key={document.id}>
                      {truncateString(document.name, 100)}
                    </Option>
                  ))}
                </Select>
                {values.document && (
                  <span
                    style={{ cursor: 'pointer', display: 'contents' }}
                    onClick={() =>
                      props.showDocumentPreviewModal({
                        name: values?.document?.label || '',
                        id: values?.document?.value || '',
                      })
                    }
                  >
                    <LinkIcon />
                  </span>
                )}
              </FormBaseFieldGroup>
            </FormBaseAntSelectWrapper>
          </FormBaseRow>
          {(errors as AddClientBankAccount).documentId && (
            <FormErrorMessage>{(errors as AddClientBankAccount).documentId}</FormErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button key='button' type='button' onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            disabled={isSubmitting || !isValid}
            key='submit'
            type='submit'
            onClick={handleSubmitForm}
          >
            Save
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

function dataToSubmit(
  values: AddClientBankAccountFormValues | UpdateClientBankAccountFormValues
): FormSubmitValues<AddClientBankAccount | UpdateClientBankAccount> {
  if ('id' in values && values.id) {
    const updateClientBankAccountValues: UpdateClientBankAccountFormValues = Object.assign(
      {},
      values
    )
    const updateValues = {
      ...updateClientBankAccountValues,
      documentId: updateClientBankAccountValues.document?.value || null,
      statusId: updateClientBankAccountValues.statusId || 20,
    }
    delete updateValues.document
    return updateValues
  }
  const submitValues: AddClientBankAccountFormValues = Object.assign({}, values)
  const addValues = {
    ...submitValues,
    documentId: submitValues.document?.value || null,
  }
  delete addValues.document
  return addValues
}

interface OuterProps {
  currentBankAccount?: ClientBankAccount
  client: ClientDetailedDto
  documents: NameDto<string>[]
  validationOptions: BankValidationDto | undefined

  onSubmit(values: FormSubmitValues<AddClientBankAccount | UpdateClientBankAccount>): Promise<void>

  showDocumentPreviewModal(doc: NameDto<string>): void

  cancel(): void
}

type AddClientBankAccountFormValues = Omit<AddClientBankAccount, 'documentId'> & {
  document?: { key: string; label: string; value: string }
}
type UpdateClientBankAccountFormValues = Omit<UpdateClientBankAccount, 'documentId'> & {
  document?: { key: string; label: string; value: string }
}

export const AddEditBankAccountForm = withFormik<
  OuterProps,
  AddClientBankAccountFormValues | UpdateClientBankAccountFormValues
>({
  mapPropsToValues: ({ currentBankAccount, client }) => {
    return {
      clientId: client.id,
      id: currentBankAccount ? currentBankAccount?.id : undefined,
      bankName: currentBankAccount?.bankName || '',
      bankAddress: currentBankAccount?.bankAddress || '',
      bankAccount: currentBankAccount?.bankAccount || '',
      accountName: currentBankAccount?.accountName || '',
      bankCode: currentBankAccount?.bankCode || '',
      codeSort: currentBankAccount?.bankCodeSort || '',
      comment: currentBankAccount?.comment || '',
      statusId: currentBankAccount?.status.id,
      document: currentBankAccount?.document
        ? {
            key: currentBankAccount.document.id,
            label: currentBankAccount.document.name,
            value: currentBankAccount.document.id,
          }
        : undefined,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const { validationOptions } = props

    const errors: FormikErrors<AddClientBankAccount | UpdateClientBankAccount> = {}
    const validate = validateField(errors, values)

    validate('bankAccount', validationOptions?.bankAccountValidation, 'Bank Account')
    validate('accountName', validationOptions?.accountNameValidation, 'Account Name')
    validate('bankName', validationOptions?.bankNameValidation, 'Bank Name')
    validate('bankCode', validationOptions?.bankCodeValidation, 'Bank Code')
    validate('bankAddress', validationOptions?.bankAddressValidation, 'Bank Address')
    validate('codeSort', validationOptions?.bankCodeSortValidation, 'Code Sort')

    if (!values.document) {
      errors.documentId = 'required'
    }

    if ('id' in values && values.id) {
      if (!values.statusId) {
        ;(errors as FormikErrors<UpdateClientBankAccount>).statusId = 'required'
      }
    }

    return errors
  },
  enableReinitialize: true,
  isInitialValid: ({ currentBankAccount }) => {
    return !!currentBankAccount
  },
})(AddEditBankAccountFormUI)
